import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Economic Calendar Page
 * Shows upcoming economic events that can affect markets
 * Similar to TradingView/Investing.com economic calendar
 */
import { useState, useEffect, useCallback } from 'react';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { 
  Calendar, Clock, Globe, Filter, ChevronDown, ChevronLeft, ChevronRight,
  AlertTriangle, TrendingUp, TrendingDown, Minus, RefreshCw, Star
} from 'lucide-react';

// Sample economic events data (in production, this would come from an API like Investing.com or ForexFactory)
const generateEconomicEvents = () => {
  const events = [
    // High Impact Events
    { 
      id: 1, currency: 'USD', country: 'US', flag: '🇺🇸',
      event: 'Non-Farm Payrolls', impact: 'high',
      previous: '227K', forecast: '180K', actual: null,
      description: 'Measures the change in the number of employed people during the previous month, excluding the farming industry.'
    },
    { 
      id: 2, currency: 'USD', country: 'US', flag: '🇺🇸',
      event: 'Federal Funds Rate', impact: 'high',
      previous: '5.50%', forecast: '5.50%', actual: null,
      description: 'Interest rate at which depository institutions lend balances at the Federal Reserve to other depository institutions overnight.'
    },
    { 
      id: 3, currency: 'EUR', country: 'EU', flag: '🇪🇺',
      event: 'ECB Interest Rate Decision', impact: 'high',
      previous: '4.50%', forecast: '4.50%', actual: null,
      description: 'The European Central Bank governing councils decision on where to set the benchmark interest rate.'
    },
    { 
      id: 4, currency: 'GBP', country: 'UK', flag: '🇬🇧',
      event: 'BoE Interest Rate Decision', impact: 'high',
      previous: '5.25%', forecast: '5.25%', actual: null,
      description: 'Bank of England Monetary Policy Committee vote on where to set the base interest rate.'
    },
    { 
      id: 5, currency: 'USD', country: 'US', flag: '🇺🇸',
      event: 'CPI m/m', impact: 'high',
      previous: '0.3%', forecast: '0.2%', actual: null,
      description: 'Consumer Price Index measures the change in the price of goods and services purchased by consumers.'
    },
    { 
      id: 6, currency: 'USD', country: 'US', flag: '🇺🇸',
      event: 'Core CPI m/m', impact: 'high',
      previous: '0.3%', forecast: '0.3%', actual: null,
      description: 'Core Consumer Price Index excludes food and energy prices.'
    },
    // Medium Impact Events
    { 
      id: 7, currency: 'USD', country: 'US', flag: '🇺🇸',
      event: 'Unemployment Rate', impact: 'medium',
      previous: '4.2%', forecast: '4.1%', actual: null,
      description: 'Percentage of the total work force that is unemployed and actively seeking employment.'
    },
    { 
      id: 8, currency: 'EUR', country: 'DE', flag: '🇩🇪',
      event: 'German ZEW Economic Sentiment', impact: 'medium',
      previous: '15.7', forecast: '17.5', actual: null,
      description: 'Survey of about 350 German institutional investors and analysts.'
    },
    { 
      id: 9, currency: 'GBP', country: 'UK', flag: '🇬🇧',
      event: 'GDP q/q', impact: 'medium',
      previous: '0.1%', forecast: '0.2%', actual: null,
      description: 'Gross Domestic Product measures the annualized change in the inflation-adjusted value of all goods and services.'
    },
    { 
      id: 10, currency: 'JPY', country: 'JP', flag: '🇯🇵',
      event: 'BoJ Policy Rate', impact: 'high',
      previous: '0.25%', forecast: '0.25%', actual: null,
      description: 'Bank of Japan short-term interest rate target.'
    },
    { 
      id: 11, currency: 'AUD', country: 'AU', flag: '🇦🇺',
      event: 'Employment Change', impact: 'medium',
      previous: '35.6K', forecast: '25.0K', actual: null,
      description: 'Change in the number of employed people.'
    },
    { 
      id: 12, currency: 'CAD', country: 'CA', flag: '🇨🇦',
      event: 'BoC Interest Rate Decision', impact: 'high',
      previous: '5.00%', forecast: '5.00%', actual: null,
      description: 'Bank of Canada overnight rate target.'
    },
    // Low Impact Events
    { 
      id: 13, currency: 'USD', country: 'US', flag: '🇺🇸',
      event: 'Building Permits', impact: 'low',
      previous: '1.42M', forecast: '1.45M', actual: null,
      description: 'Annualized number of new residential building permits issued.'
    },
    { 
      id: 14, currency: 'EUR', country: 'EU', flag: '🇪🇺',
      event: 'Industrial Production m/m', impact: 'low',
      previous: '-0.5%', forecast: '0.3%', actual: null,
      description: 'Change in the total inflation-adjusted value of output produced by manufacturers, mines, and utilities.'
    },
    { 
      id: 15, currency: 'CHF', country: 'CH', flag: '🇨🇭',
      event: 'SNB Interest Rate Decision', impact: 'high',
      previous: '1.75%', forecast: '1.50%', actual: null,
      description: 'Swiss National Bank interest rate decision.'
    },
    { 
      id: 16, currency: 'TRY', country: 'TR', flag: '🇹🇷',
      event: 'TCMB Interest Rate Decision', impact: 'high',
      previous: '50.00%', forecast: '50.00%', actual: null,
      description: 'Central Bank of the Republic of Turkey one-week repo rate.'
    },
    { 
      id: 17, currency: 'TRY', country: 'TR', flag: '🇹🇷',
      event: 'CPI y/y', impact: 'high',
      previous: '58.51%', forecast: '55.00%', actual: null,
      description: 'Turkey Consumer Price Index annual change.'
    },
    { 
      id: 18, currency: 'CNY', country: 'CN', flag: '🇨🇳',
      event: 'PBoC Interest Rate Decision', impact: 'medium',
      previous: '3.45%', forecast: '3.45%', actual: null,
      description: "People's Bank of China loan prime rate."
    },
  ];

  // Generate times for today and next few days
  const now = new Date();
  return events.map((event, index) => {
    const eventDate = new Date(now);
    eventDate.setDate(eventDate.getDate() + Math.floor(index / 4));
    eventDate.setHours(8 + (index % 12), (index * 15) % 60, 0);
    
    return {
      ...event,
      time: eventDate.toISOString(),
      // Randomly set some events as "actual" released
      actual: index < 3 ? (parseFloat(event.forecast) + (Math.random() - 0.5) * 0.5).toFixed(1) + (event.forecast.includes('%') ? '%' : 'K') : null
    };
  });
};

const TraderEconomicCalendar = () => {
  const tx = useTraderText();
  const { isDark } = useTraderTheme();
  const { t } = useTraderLanguage();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [impactFilter, setImpactFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [showImpactDropdown, setShowImpactDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [favorites, setFavorites] = useState([]);

  // Load events
  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setEvents(generateEconomicEvents());
      setLoading(false);
    }, 500);
  }, [selectedDate]);

  // Filter events
  const filteredEvents = events.filter(event => {
    if (impactFilter !== 'all' && event.impact !== impactFilter) return false;
    if (currencyFilter !== 'all' && event.currency !== currencyFilter) return false;
    return true;
  });

  // Group events by date
  const groupedEvents = filteredEvents.reduce((acc, event) => {
    const date = new Date(event.time).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  // Impact indicator component
  const ImpactIndicator = ({ impact }) => {
    const colors = {
      high: 'bg-red-500',
      medium: 'bg-orange-500',
      low: 'bg-yellow-500'
    };
    
    return (
      <div className="flex gap-0.5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`w-1.5 h-4 rounded-sm ${
              (impact === 'high' || (impact === 'medium' && i < 2) || (impact === 'low' && i < 1))
                ? colors[impact]
                : isDark ? 'bg-gray-700' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  // Format time in Turkey timezone
  const formatEventTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(tx('en-US'), {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date header
  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return tx('Today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return tx('Tomorrow');
    }
    
    return date.toLocaleDateString(tx('en-US'), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Toggle favorite
  const toggleFavorite = (eventId) => {
    setFavorites(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  // Unique currencies for filter
  const currencies = [...new Set(events.map(e => e.currency))];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="text-cyan-500" size={28} />
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Economic Calendar ")}</h1>
          </div>
          <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Clock size={14} />{tx("Turkey Time (UTC+3) ")}</div>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-4 p-4 rounded-xl mb-6 ${
          isDark ? 'bg-[#111827] border border-gray-800' : 'bg-white border border-gray-200'
        }`}>
          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const newDate = new Date(selectedDate);
                newDate.setDate(newDate.getDate() - 7);
                setSelectedDate(newDate);
              }}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <ChevronLeft size={18} className={isDark ? 'text-gray-400' : 'text-gray-600'} />
            </button>
            <button
              onClick={() => setSelectedDate(new Date())}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'
              }`}
            >{tx("This Week ")}</button>
            <button
              onClick={() => {
                const newDate = new Date(selectedDate);
                newDate.setDate(newDate.getDate() + 7);
                setSelectedDate(newDate);
              }}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <ChevronRight size={18} className={isDark ? 'text-gray-400' : 'text-gray-600'} />
            </button>
          </div>

          <div className="h-6 w-px bg-gray-600" />

          {/* Impact Filter */}
          <div className="relative">
            <button
              onClick={() => setShowImpactDropdown(!showImpactDropdown)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border ${
                isDark 
                  ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <AlertTriangle size={14} />{tx("Impact ")}{impactFilter !== 'all' && (
                <span className={`px-1.5 py-0.5 text-xs rounded capitalize ${
                  impactFilter === 'high' ? 'bg-red-500/20 text-red-400' :
                  impactFilter === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {impactFilter}
                </span>
              )}
              <ChevronDown size={14} />
            </button>
            
            {showImpactDropdown && (
              <div className={`absolute top-full left-0 mt-1 w-36 rounded-lg shadow-lg z-10 ${
                isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
                {['all', 'high', 'medium', 'low'].map(impact => (
                  <button
                    key={impact}
                    onClick={() => {
                      setImpactFilter(impact);
                      setShowImpactDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm capitalize ${
                      impactFilter === impact
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : isDark 
                          ? 'text-gray-300 hover:bg-gray-700' 
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {tx(impact === 'all' ? 'All Impact' : impact)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Currency Filter */}
          <div className="relative">
            <button
              onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border ${
                isDark 
                  ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Globe size={14} />{tx("Currency ")}{currencyFilter !== 'all' && (
                <span className="px-1.5 py-0.5 text-xs bg-cyan-500/20 text-cyan-400 rounded">
                  {currencyFilter}
                </span>
              )}
              <ChevronDown size={14} />
            </button>
            
            {showCurrencyDropdown && (
              <div className={`absolute top-full left-0 mt-1 w-32 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto ${
                isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
                <button
                  onClick={() => {
                    setCurrencyFilter('all');
                    setShowCurrencyDropdown(false);
                  }}
                  className={`w-full px-4 py-2 text-left text-sm ${
                    currencyFilter === 'all'
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : isDark 
                        ? 'text-gray-300 hover:bg-gray-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >{tx("All ")}</button>
                {currencies.map(currency => (
                  <button
                    key={currency}
                    onClick={() => {
                      setCurrencyFilter(currency);
                      setShowCurrencyDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm ${
                      currencyFilter === currency
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : isDark 
                          ? 'text-gray-300 hover:bg-gray-700' 
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {currency}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1" />

          {/* Refresh */}
          <button
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                setEvents(generateEconomicEvents());
                setLoading(false);
              }, 500);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
              isDark 
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />{tx("Refresh ")}</button>
        </div>

        {/* Legend */}
        <div className={`flex items-center gap-6 mb-4 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              <div className="w-1.5 h-4 rounded-sm bg-red-500" />
              <div className="w-1.5 h-4 rounded-sm bg-red-500" />
              <div className="w-1.5 h-4 rounded-sm bg-red-500" />
            </div>{tx("High Impact ")}</div>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              <div className="w-1.5 h-4 rounded-sm bg-orange-500" />
              <div className="w-1.5 h-4 rounded-sm bg-orange-500" />
              <div className={`w-1.5 h-4 rounded-sm ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
            </div>{tx("Medium Impact ")}</div>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              <div className="w-1.5 h-4 rounded-sm bg-yellow-500" />
              <div className={`w-1.5 h-4 rounded-sm ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
              <div className={`w-1.5 h-4 rounded-sm ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
            </div>{tx("Low Impact ")}</div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-cyan-500" size={32} />
          </div>
        ) : (
          Object.entries(groupedEvents).map(([date, dateEvents]) => (
            <div key={date} className="mb-6">
              {/* Date Header */}
              <div className={`flex items-center gap-3 mb-3 px-2`}>
                <div className={`text-sm font-semibold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  {formatDateHeader(date)}
                </div>
                <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>

              {/* Events */}
              <div className={`rounded-xl overflow-hidden ${
                isDark ? 'bg-[#111827] border border-gray-800' : 'bg-white border border-gray-200'
              }`}>
                {dateEvents.map((event, index) => (
                  <div
                    key={event.id}
                    className={`flex items-center gap-4 px-4 py-3 ${
                      index > 0 ? (isDark ? 'border-t border-gray-800' : 'border-t border-gray-100') : ''
                    } ${isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    {/* Time */}
                    <div className={`w-16 text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatEventTime(event.time)}
                    </div>

                    {/* Country Flag */}
                    <div className="w-8 text-lg">{event.flag}</div>

                    {/* Currency */}
                    <div className={`w-12 text-xs font-medium px-2 py-0.5 rounded text-center ${
                      isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {event.currency}
                    </div>

                    {/* Impact */}
                    <div className="w-8">
                      <ImpactIndicator impact={event.impact} />
                    </div>

                    {/* Event Name */}
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {tx(event.event)}
                      </div>
                      <div className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {tx(event.description)}
                      </div>
                    </div>

                    {/* Values */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="w-20 text-right">
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Actual")}</div>
                        <div className={event.actual ? (
                          parseFloat(event.actual) > parseFloat(event.forecast) 
                            ? 'text-green-400 font-medium' 
                            : parseFloat(event.actual) < parseFloat(event.forecast)
                              ? 'text-red-400 font-medium'
                              : isDark ? 'text-white' : 'text-gray-900'
                        ) : isDark ? 'text-gray-500' : 'text-gray-400'}>
                          {event.actual || '--'}
                        </div>
                      </div>
                      <div className="w-20 text-right">
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Forecast")}</div>
                        <div className={isDark ? 'text-white' : 'text-gray-900'}>{event.forecast}</div>
                      </div>
                      <div className="w-20 text-right">
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Previous")}</div>
                        <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>{event.previous}</div>
                      </div>
                    </div>

                    {/* Favorite */}
                    <button
                      onClick={() => toggleFavorite(event.id)}
                      className={`p-1 rounded ${
                        favorites.includes(event.id)
                          ? 'text-yellow-400'
                          : isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-500'
                      }`}
                    >
                      <Star size={16} fill={favorites.includes(event.id) ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Empty State */}
        {!loading && Object.keys(groupedEvents).length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Calendar size={48} className="mx-auto mb-4 opacity-50" />
            <p>{tx("No economic events found for the selected filters.")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TraderEconomicCalendar;
